import ExportButton from "components/Button/ExportButton/ExportButton";
import { Paper } from "helpers/api";
import { IBookmarkItem, IPaperBookmark } from "helpers/bookmark";
import pluralize from "helpers/pluralize";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";
import useLabels from "hooks/useLabels";
import { useCallback } from "react";
import usePaperBookmarks from "../hooks/usePaperBookmarks";
import BookmarkEmpty from "./BookmarkEmpty";
import SearchResultsList from "features/SearchResultsList";

type PaperBookmarkListProps = {
  bookmarkListTitle: string;
  bookmarks: IBookmarkItem[];
  handleClickRemoveBookmark: (item: IBookmarkItem) => void;
};

/**
 * @component PaperBookmarkList
 * @description A list of bookmarked papers. Loads data from the paper details endpoint to populate the SearchResults component.
 */
function PaperBookmarkList({
  bookmarkListTitle,
  bookmarks,
  handleClickRemoveBookmark,
}: PaperBookmarkListProps) {
  const [labels] = useLabels("features.bookmark-lists.bookmark-list-detail");

  const { data: papers, isLoading } = usePaperBookmarks(bookmarks);

  const handleClickSavePaperItem = useCallback(
    async (item: Paper) => {
      const bookmarkItem = bookmarks.find(
        (bookmark) =>
          (bookmark.bookmark_data as IPaperBookmark).paper_id === item.paper_id
      );

      if (bookmarkItem != null) {
        handleClickRemoveBookmark(bookmarkItem);
      }
    },
    [bookmarks, handleClickRemoveBookmark]
  );

  if (bookmarks.length === 0) {
    return <BookmarkEmpty tab="papers" />;
  }

  return (
    <>
      <div>
        <div className="flex items-center py-2 border-b min-h-14 border-border-base">
          <div className="flex-1 base-bold">
            {isLoading
              ? labels["papers"]
              : pluralize(labels["n-papers"], {
                  PAPER_COUNT: papers?.length ?? 0,
                })}
          </div>
          <div className="hidden justify-end items-center md:flex">
            <ExportButton
              title={bookmarkListTitle}
              disabled={isLoading}
              items={papers ?? []}
              actionSource={MixpanelActionSource.BookmarkList}
            />
          </div>
        </div>
        <SearchResultsList
          isLoading={isLoading}
          showCiteBtn
          showSaveBtn
          showShareBtn
          onClickSave={(item: Paper) => handleClickSavePaperItem(item)}
          mixpanelActionSource={MixpanelActionSource.BookmarkList}
          items={papers ?? []}
          resultIdToYesNoAnswer={{}}
        />
      </div>
    </>
  );
}

export default PaperBookmarkList;
